import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Copy, Wifi } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { QrCode } from '@/components/ui/qr-code';
import { useAppSelector } from '@/Redux/store';

interface WifiInfo {
  ssid: string | null;
  password: string | null;
  /** `WIFI:T:WPA;S:…;P:…;;` — the string a camera turns into a join prompt. */
  qrPayload: string | null;
}

/** Shown only after the guest session is authenticated. */
export default function WifiPage() {
  const { t } = useTranslation();
  const session = useAppSelector((s) => s.guest.session);
  const [info, setInfo] = useState<WifiInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session?.sessionId) return;
    // The guest surface is keyed by session, not branch: the open session is the
    // authorization, and it decides which area's network is returned.
    api
      .get<WifiInfo>(`/wifi/sessions/${session.sessionId}`)
      .then(({ data }) => setInfo(data))
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, [session?.sessionId]);

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('wifi.copy'));
    } catch {
      // Clipboard access is denied on an insecure origin; the guest can still
      // read the password off the screen, so this is not worth an error toast.
    }
  };

  if (loading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">{t('common.loading')}</p>;
  }

  if (!info?.ssid) {
    return <p className="py-16 text-center text-sm text-muted-foreground">{t('guest.wifi.empty')}</p>;
  }

  return (
    <Card>
      <CardContent className="space-y-5 p-6">
        <Wifi className="mx-auto h-10 w-10 text-accent" />

        {/* The payload rendered as an actual scannable code. */}
        {info.qrPayload && (
          <div className="space-y-2 text-center">
            <QrCode
              value={info.qrPayload}
              size={220}
              className="mx-auto border p-2"
              title={t('guest.wifi.scanToJoin')}
            />
            <p className="text-xs text-muted-foreground">{t('guest.wifi.scanToJoin')}</p>
          </div>
        )}

        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">{t('wifi.network')}</div>
          <div className="font-medium">{info.ssid}</div>
        </div>

        {info.password && (
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">{t('wifi.password')}</div>
            <div className="flex items-center gap-2">
              <code dir="ltr" className="min-w-0 flex-1 break-all rounded-lg bg-surface px-3 py-2 font-mono text-sm">
                {info.password}
              </code>
              <Button
                size="icon"
                variant="outline"
                aria-label={t('wifi.copy')}
                onClick={() => void copy(info.password!)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
